import { getOnboardingSessionOptions } from '@labflow/api-client';
import { Button, Dialog, TooltipProvider } from '@labflow/ui';
import { Toaster } from '@labflow/ui/toast';
import { useQuery } from '@tanstack/react-query';
import type { QueryClient } from '@tanstack/react-query';
import {
  Outlet,
  createRootRouteWithContext,
  useNavigate,
  useRouterState,
} from '@tanstack/react-router';
import { lazy, Suspense, useEffect, useRef, useState } from 'react';

import { RouteErrorFallback } from '../components/route-error-fallback';
import { AppHeader } from '../components/shell/app-header';
import { ScreenFrame } from '../components/shell/screen-frame';
import { SubNav } from '../components/shell/sub-nav';
import { HeaderUser } from '../features/auth/header-user';
import { useSession } from '../features/auth/use-session';
import { FALLBACK_ROUTE, isOnboardingRoute } from '../features/onboarding/require-onboarding';
import { shouldShowAgentation } from '../lib/agentation-gate';
import { MYLAB_SUBNAV, currentModule, currentScreenLabel } from '../lib/navigation';
import { serverFeaturesEnabled } from '../lib/server-features';

export interface RouterContext {
  queryClient: QueryClient;
}

const BASE_DOCUMENT_TITLE = 'Labflow';

/** 라우트가 바뀌면 화면 이름을 문서 제목과 polite 알림에 싣는다 (D145 ⑨). 첫 진입은 알리지 않는다. */
function useScreenAnnouncement(pathname: string): string | undefined {
  const label = currentScreenLabel(pathname);
  const [announcement, setAnnouncement] = useState<string>();
  const previousPath = useRef(pathname);

  useEffect(() => {
    document.title = label ? `${label} · ${BASE_DOCUMENT_TITLE}` : BASE_DOCUMENT_TITLE;
    if (previousPath.current === pathname) return;
    previousPath.current = pathname;
    setAnnouncement(label);
  }, [label, pathname]);

  return announcement;
}

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootLayout,
  errorComponent: RouteErrorFallback,
  onCatch: (error) => {
    // oxlint-disable-next-line no-console -- 운영 환경에서도 콘솔로는 원인을 남긴다.
    console.error(error);
  },
});

/**
 * 게이팅된 계정이 아니면 청크 자체를 내려받지 않는다 — 정적 import는 모든 방문자의
 * 번들에 실린다(D102).
 */
const Agentation = lazy(() =>
  import('agentation').then((module) => ({ default: module.Agentation })),
);

function RootLayout() {
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  // 게스트 링크는 전역 탐색이나 다른 화면을 노출하지 않는다(S18 §4, `#211`) — 세션도 없는
  // 익명 방문이므로 이 경로에서는 `/v1/me`도 부르지 않는다.
  const isGuestRoute = pathname.startsWith('/guest/');
  const { user } = useSession({ enabled: !isGuestRoute });
  const showAgentation = shouldShowAgentation(user?.primary_email);
  const [pendingLabel, setPendingLabel] = useState<string | null>(null);
  const navigate = useNavigate();
  const incomplete = Boolean(user) && !user?.onboarding_completed_at;
  const session = useQuery({ ...getOnboardingSessionOptions(), enabled: incomplete });
  const announcement = useScreenAnnouncement(pathname);
  const activeModule = currentModule(pathname);
  // 온보딩 미완료 사용자에게는 들어갈 수 없는 화면을 가리키지 않는다 (D145 ⑦)
  const showMyLabSubNav = activeModule?.to === '/mylab' && !incomplete;
  // 로그아웃 상태의 헤더는 로고와 로그인만 — 로그인 화면에서는 그 로그인도 같은 화면이라 두지 않는다 (D154 ①)
  const showHeaderUser = Boolean(user) || pathname !== '/login';

  function goToOnboarding() {
    setPendingLabel(null);
    const route = session.data?.next_route ?? null;
    void navigate({ to: isOnboardingRoute(route) ? route : FALLBACK_ROUTE });
  }

  return (
    <TooltipProvider>
      {/* L2-06 §8: 최상위 페이지는 뷰포트 높이를 넘지 않고, 콘텐츠는 각 영역 안에서 스크롤한다. */}
      <ScreenFrame
        announcement={announcement}
        header={
          isGuestRoute ? null : (
            <AppHeader
              activeModule={activeModule}
              onModuleClick={(item) => {
                if (!item.gated || !incomplete) return false;
                setPendingLabel(item.label);
                return true;
              }}
              // 프리뷰 빌드는 세션 없이 화면을 둘러보는 배포라 모듈을 그대로 둔다 (D70)
              showModules={Boolean(user) || !serverFeaturesEnabled}
              user={showHeaderUser ? <HeaderUser /> : null}
            />
          )
        }
        subNav={
          showMyLabSubNav && !isGuestRoute ? (
            <SubNav label="My Lab 메뉴" items={MYLAB_SUBNAV} />
          ) : null
        }
      >
        <Outlet />
      </ScreenFrame>

      <Dialog.Root
        open={pendingLabel !== null}
        onOpenChange={(open) => {
          if (open) return;
          setPendingLabel(null);
          if (incomplete) void navigate({ to: '/' });
        }}
      >
        <Dialog.Content
          title="온보딩을 먼저 완료해 주세요"
          description={`온보딩을 마치면 ${pendingLabel ?? '이'} 화면을 쓸 수 있어요.`}
        >
          <Dialog.Footer>
            <Dialog.Close render={<Button variant="ghost">닫기</Button>} />
            <Button onClick={goToOnboarding}>온보딩 시작</Button>
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog.Root>

      {showAgentation ? (
        <Suspense fallback={null}>
          <Agentation />
        </Suspense>
      ) : null}
      <Toaster />
    </TooltipProvider>
  );
}
